import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Container, VStack, Heading, Text, Box, Badge, HStack,
  OrderedList, ListItem, Divider, Button, Link, SimpleGrid, Icon,
} from "@chakra-ui/react";
import { FiArrowLeft, FiArrowRight, FiGlobe, FiShield, FiMail, FiTrash2, FiClock, FiCopy, FiZap } from "react-icons/fi";
import { NavbarWithCallToAction } from "../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";

const LaunchDomainLifecycle: React.FC = () => {
  return (
    <>
      <NavbarWithCallToAction />
      <Container maxW="container.md" py={10}>
        <VStack spacing={6} align="stretch">
          {/* Back link */}
          <Link as={RouterLink} to="/updates" _hover={{ textDecoration: "none" }}>
            <Button variant="ghost" size="sm" leftIcon={<FiArrowLeft />} color="gray.500">
              All Updates
            </Button>
          </Link>

          {/* Header */}
          <VStack spacing={3} align="start">
            <HStack>
              <Badge colorScheme="purple">Launch Module</Badge>
              <Badge colorScheme="blue">v1.2.0</Badge>
              <Text fontSize="xs" color="gray.400">24 Mar 2026</Text>
            </HStack>
            <Heading size="lg" color="#2c5282">Domain Lifecycle & Site Management</Heading>
            <Text color="gray.600">
              Full domain management from one page — point nameservers, auto-configure email DNS, monitor deployment history, and clean up infrastructure when you're done. No more switching between Vercel, Postmark, and GitHub dashboards.
            </Text>
          </VStack>

          <Divider />

          {/* What's New */}
          <Box>
            <Heading size="md" color="#2b6cb0" mb={3}>What's New</Heading>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <Box p={4} borderRadius="md" border="1px solid" borderColor="purple.200" bg="purple.50">
                <HStack mb={2}>
                  <Icon as={FiGlobe} color="purple.500" />
                  <Text fontWeight="bold" color="purple.700">Full Domain Lifecycle</Text>
                </HStack>
                <Text fontSize="sm" color="gray.600">
                  Add your custom domain, get Vercel nameservers with click-to-copy, and auto-check DNS propagation every 10 seconds until it's live.
                </Text>
              </Box>
              <Box p={4} borderRadius="md" border="1px solid" borderColor="green.200" bg="green.50">
                <HStack mb={2}>
                  <Icon as={FiMail} color="green.500" />
                  <Text fontWeight="bold" color="green.700">One-Click Email DNS</Text>
                </HStack>
                <Text fontSize="sm" color="gray.600">
                  Once DNS is pointing to Vercel, click one button to configure Postmark DKIM, Return-Path, ImprovMX MX records, SPF, and DMARC automatically.
                </Text>
              </Box>
              <Box p={4} borderRadius="md" border="1px solid" borderColor="blue.200" bg="blue.50">
                <HStack mb={2}>
                  <Icon as={FiShield} color="blue.500" />
                  <Text fontWeight="bold" color="blue.700">Domain Health Dashboard</Text>
                </HStack>
                <Text fontSize="sm" color="gray.600">
                  Per-domain health badges show DNS, DKIM, Return-Path, MX, and SPF status at a glance. Green means configured, red means action needed.
                </Text>
              </Box>
              <Box p={4} borderRadius="md" border="1px solid" borderColor="orange.200" bg="orange.50">
                <HStack mb={2}>
                  <Icon as={FiClock} color="orange.500" />
                  <Text fontWeight="bold" color="orange.700">Deployment History</Text>
                </HStack>
                <Text fontSize="sm" color="gray.600">
                  See your last 10 Vercel deployments with status badges, commit messages, timestamps, and preview links. Auto-refreshes every 30 seconds.
                </Text>
              </Box>
              <Box p={4} borderRadius="md" border="1px solid" borderColor="red.200" bg="red.50">
                <HStack mb={2}>
                  <Icon as={FiTrash2} color="red.500" />
                  <Text fontWeight="bold" color="red.700">Clean Delete</Text>
                </HStack>
                <Text fontSize="sm" color="gray.600">
                  Deleting a site now removes the GitHub repo, Vercel project, Postmark server, and DNS zone. A confirmation dialog shows exactly what will be cleaned up.
                </Text>
              </Box>
              <Box p={4} borderRadius="md" border="1px solid" borderColor="teal.200" bg="teal.50">
                <HStack mb={2}>
                  <Icon as={FiZap} color="teal.500" />
                  <Text fontWeight="bold" color="teal.700">Auto-Setup on Provision</Text>
                </HStack>
                <Text fontSize="sm" color="gray.600">
                  Custom domains provided during site creation now get their DNS zone auto-registered with Vercel, so nameserver instructions are ready immediately.
                </Text>
              </Box>
            </SimpleGrid>
          </Box>

          {/* How to Use */}
          <Box>
            <Heading size="md" color="#2b6cb0" mb={3}>How to Use</Heading>
            <OrderedList spacing={3} pl={4}>
              <ListItem>
                <Text color="gray.600">Go to <strong>Launch</strong> and click into a deployed site.</Text>
              </ListItem>
              <ListItem>
                <Text color="gray.600">In <strong>Domain Management</strong>, add your custom domain (e.g. <code>mybusiness.com.au</code>).</Text>
              </ListItem>
              <ListItem>
                <Text color="gray.600">Click the domain to expand it — copy the nameservers and update them at your registrar.</Text>
              </ListItem>
              <ListItem>
                <Text color="gray.600">Wait for DNS to propagate (auto-checks every 10s) — the DNS badge will turn green.</Text>
              </ListItem>
              <ListItem>
                <Text color="gray.600">Click <strong>Configure Email DNS</strong> to auto-add all email records in one click.</Text>
              </ListItem>
              <ListItem>
                <Text color="gray.600">Click <strong>Manage Email Accounts</strong> to create email addresses on the tenant site.</Text>
              </ListItem>
              <ListItem>
                <Text color="gray.600">Check <strong>Deployments</strong> to see build history and click <strong>Rebuild</strong> to trigger a fresh deploy.</Text>
              </ListItem>
            </OrderedList>
          </Box>

          <Divider />

          {/* Try It Now */}
          <Box bg="green.50" p={5} borderRadius="md" border="1px solid" borderColor="green.200">
            <Heading size="md" color="#38a169" mb={3}>Try It Now!</Heading>
            <Text mb={3} color="gray.600">Head to the Launch module to manage your deployed sites and domains.</Text>
            <Link as={RouterLink} to="/launch" _hover={{ textDecoration: "none" }}>
              <Button colorScheme="green" size="md" rightIcon={<FiArrowRight />}>
                Open Launch Module
              </Button>
            </Link>
          </Box>

          {/* Sign off */}
          <Box>
            <Text color="gray.600">As always, reach out if you have any questions or feedback.</Text>
            <Text mt={4} color="gray.600">
              Regards,<br />
              <strong>Dr Thomas Miller</strong><br />
              <Text as="em" color="gray.500">Business Builder</Text>
            </Text>
          </Box>
        </VStack>
      </Container>
      <FooterWithFourColumns />
    </>
  );
};

export default LaunchDomainLifecycle;
